import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DOW = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

const STATUS_COLORS = {
  Present: "#10B981",
  Absent: "#DC2626",
  "Half-Day": "#FBBF24",
  Leave: "#6B7280",
  Holiday: "#8B5CF6",
};

export default function AttendanceHeatmap({ refreshKey = 0 }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1..12
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState(""); // empty = team view
  const [data, setData] = useState(null);

  useEffect(() => {
    if (isAdmin) api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => {});
  }, [isAdmin]);

  useEffect(() => {
    const params = { year, month };
    if (isAdmin && employeeId) params.employee_id = employeeId;
    api.get("/attendance/heatmap", { params }).then(({ data }) => setData(data)).catch(() => setData(null));
  }, [year, month, employeeId, isAdmin, refreshKey]);

  const changeMonth = (delta) => {
    let m = month + delta;
    let y = year;
    if (m < 1) { m = 12; y -= 1; }
    if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  // Build weeks-grid: array of arrays (each row = 7 days, leading nulls for first-week padding)
  const grid = useMemo(() => {
    if (!data) return [];
    const firstDow = new Date(year, month - 1, 1).getDay(); // 0=Sun
    const cells = Array(firstDow).fill(null).concat(data.days);
    const rows = [];
    for (let i = 0; i < cells.length; i += 7) rows.push(cells.slice(i, i + 7));
    // pad last row
    if (rows.length && rows[rows.length - 1].length < 7) {
      rows[rows.length - 1] = rows[rows.length - 1].concat(Array(7 - rows[rows.length - 1].length).fill(null));
    }
    return rows;
  }, [data, year, month]);

  return (
    <div className="bg-white border border-[#E5E7EB]" data-testid="heatmap-panel">
      <div className="px-6 py-4 border-b border-[#E5E7EB] flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps">Calendar heatmap</div>
          <div className="font-display font-bold text-xl mt-1">Attendance trends</div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {isAdmin && (
            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}
                    data-testid="heatmap-employee-select"
                    className="border border-[#E5E7EB] px-3 py-2 text-sm">
              <option value="">Whole team</option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>{e.employee_id} — {e.name}</option>
              ))}
            </select>
          )}
          <div className="flex items-center border border-[#E5E7EB]">
            <button data-testid="heatmap-prev-month" onClick={() => changeMonth(-1)}
                    className="p-2 hover:bg-[#F9FAFB]"><CaretLeft size={14} /></button>
            <div className="px-4 py-2 text-sm font-semibold min-w-[160px] text-center" data-testid="heatmap-month-label">
              {MONTHS[month - 1]} {year}
            </div>
            <button data-testid="heatmap-next-month" onClick={() => changeMonth(1)}
                    className="p-2 hover:bg-[#F9FAFB]"><CaretRight size={14} /></button>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="max-w-3xl">
        {/* Day of week header */}
        <div className="grid grid-cols-7 gap-1 mb-2">
          {DOW.map((d) => (
            <div key={d} className="label-caps text-center py-1">{d}</div>
          ))}
        </div>

        {/* Weeks */}
        <div className="grid grid-cols-7 gap-1">
          {grid.flat().map((cell, idx) => (
            <HeatCell key={cell ? cell.date : `pad-${idx}`} cell={cell} mode={data?.mode} totalEmp={data?.total_employees || 0} />
          ))}
        </div>
        </div>

        {/* Legend */}
        <div className="mt-6 flex flex-wrap items-center gap-4 text-xs">
          {data?.mode === "single" ? (
            <>
              {Object.entries(STATUS_COLORS).map(([label, color]) => (
                <div key={label} className="flex items-center gap-2">
                  <span className="inline-block w-3 h-3" style={{ background: color }} />
                  <span className="text-[#6B7280]">{label}</span>
                </div>
              ))}
              <div className="flex items-center gap-2">
                <span className="inline-block w-3 h-3 border border-[#E5E7EB]" />
                <span className="text-[#6B7280]">Not marked</span>
              </div>
            </>
          ) : (
            <>
              <div className="text-[#6B7280]">Present-rate</div>
              {[0, 0.3, 0.6, 0.85, 1].map((p) => (
                <div key={p} className="flex items-center gap-2">
                  <span className="inline-block w-3 h-3" style={{ background: presentRateColor(p) }} />
                  <span className="text-[#6B7280]">{Math.round(p * 100)}%</span>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function HeatCell({ cell, mode, totalEmp }) {
  if (!cell) return <div className="aspect-square" />;
  const day = parseInt(cell.date.slice(-2), 10);

  if (mode === "single") {
    if (cell.pre_join) {
      return (
        <div
          data-testid={`heat-cell-${cell.date}`}
          title={`${cell.date}: before joining`}
          className="aspect-square border border-dashed border-[#E5E7EB] p-1.5 relative bg-[#FAFAFA]"
        >
          <span className="text-xs font-mono text-[#D1D5DB]">{day}</span>
        </div>
      );
    }
    const color = cell.status ? STATUS_COLORS[cell.status] : null;
    const holidayTitle = cell.is_holiday && cell.holiday_name ? ` · ${cell.holiday_name}` : "";
    return (
      <div
        data-testid={`heat-cell-${cell.date}`}
        title={
          cell.is_holiday
            ? `${cell.date}: Holiday${holidayTitle}`
            : cell.status
              ? `${cell.date}: ${cell.status}`
              : `${cell.date}: not marked`
        }
        className={`aspect-square border ${color ? "border-transparent" : "border-[#E5E7EB]"} p-1.5 relative transition-colors duration-200`}
        style={{ background: color ? `${color}` : "#FAFAFA" }}
      >
        <span className={`text-xs font-mono font-semibold ${color ? "text-white" : "text-[#9CA3AF]"}`}>{day}</span>
        {cell.is_holiday && (
          <span className="absolute bottom-1 right-1 text-[8px] font-bold text-white/90 uppercase tracking-wider">Hol</span>
        )}
      </div>
    );
  }

  // team mode
  const c = cell.counts || {};
  if (cell.is_holiday) {
    return (
      <div
        data-testid={`heat-cell-${cell.date}`}
        title={`${cell.date}: ${cell.holiday_name || "Holiday"}`}
        className="aspect-square p-1.5 border border-transparent transition-colors duration-200"
        style={{ background: "#8B5CF6" }}
      >
        <div className="text-xs font-mono font-semibold text-white">{day}</div>
        <div className="text-[9px] mt-0.5 text-white/80 uppercase tracking-wide">Holiday</div>
      </div>
    );
  }
  const present = (c.Present || 0) + 0.5 * (c["Half-Day"] || 0);
  const rate = totalEmp > 0 ? present / totalEmp : 0;
  const bg = totalEmp > 0 && (c.Present || c.Absent || c["Half-Day"] || c.Leave) ? presentRateColor(rate) : "#FAFAFA";
  const textLight = rate >= 0.3 && (c.Present || c.Absent || c["Half-Day"] || c.Leave);
  return (
    <div
      data-testid={`heat-cell-${cell.date}`}
      title={`${cell.date} · P:${c.Present||0} A:${c.Absent||0} H:${c["Half-Day"]||0} L:${c.Leave||0}`}
      className="aspect-square p-1.5 border border-transparent transition-colors duration-200"
      style={{ background: bg }}
    >
      <div className={`text-xs font-mono font-semibold ${textLight ? "text-white" : "text-[#9CA3AF]"}`}>{day}</div>
      {(c.Present || c.Absent) ? (
        <div className={`text-[10px] mt-0.5 ${textLight ? "text-white/80" : "text-[#6B7280]"}`}>
          {c.Present || 0}/{totalEmp}
        </div>
      ) : null}
    </div>
  );
}

// Interpolate between red (#DC2626) at 0 and green (#10B981) at 1 with light background at 0
function presentRateColor(rate) {
  const r = Math.max(0, Math.min(1, rate));
  // stops: 0=#FEE2E2, .5=#FDE68A, 1=#10B981
  const stops = [
    { p: 0, c: [254, 226, 226] },
    { p: 0.5, c: [253, 224, 138] },
    { p: 1, c: [16, 185, 129] },
  ];
  let a = stops[0], b = stops[stops.length - 1];
  for (let i = 0; i < stops.length - 1; i++) {
    if (r >= stops[i].p && r <= stops[i + 1].p) { a = stops[i]; b = stops[i + 1]; break; }
  }
  const t = (r - a.p) / Math.max(b.p - a.p, 0.0001);
  const mix = a.c.map((v, i) => Math.round(v + (b.c[i] - v) * t));
  return `rgb(${mix.join(",")})`;
}
